import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (n) => (n == null) ? '—' : `${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;
const fmtPct = (n) => (n == null) ? '—' : `${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
const fmtTrend = (n) => (n == null) ? '—' : `${n > 0 ? '+' : ''}${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;

// Bloc « Météo commerciale » (M-1) : podium alimenté par les données CA Corner.
export const CommercialWeatherWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const token = localStorage.getItem('capex_token');
      const res = await axios.get(`${API}/ca-corner/highlights`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      setError(true);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const Stat = ({ value, name, label, valueClass, testid }) => (
    <div className="min-w-0" data-testid={testid}>
      <p className={`font-display text-[26px] font-semibold tabular-nums leading-none ${valueClass || 'text-white'}`}>{value}</p>
      <p className="text-[13px] font-semibold text-white truncate mt-1.5 leading-tight">{name || '—'}</p>
      <p className="text-[11px] text-ondark-3 leading-none mt-1">{label}</p>
    </div>
  );

  return (
    <div
      className="relative overflow-hidden rounded-card bg-graphite-900 px-6 py-5 w-full lg:w-auto lg:min-w-[520px]"
      data-testid="commercial-weather-widget"
    >
      {/* Coin orange décoratif */}
      <div className="pointer-events-none absolute -top-8 -right-8 w-24 h-24 rounded-full bg-orange" aria-hidden="true" />
      <div className="pointer-events-none absolute top-2 right-9 w-14 h-[2px] bg-orange/40 rotate-[-35deg]" aria-hidden="true" />
      <div className="pointer-events-none absolute top-5 right-6 w-9 h-[2px] bg-orange/30 rotate-[-35deg]" aria-hidden="true" />

      <div className="relative flex items-center gap-2 mb-4 pr-16">
        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-orange">Météo commerciale</span>
        {data?.period && (
          <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-orange" data-testid="cw-period">· {data.period}</span>
        )}
      </div>
      {loading ? (
        <p className="text-sm py-3 relative text-ondark-2">Chargement…</p>
      ) : error || !data ? (
        <p className="text-sm py-3 relative text-ondark-2" data-testid="cw-unavailable">Indicateurs indisponibles</p>
      ) : (
        <div className="relative grid grid-cols-3 gap-5">
          <Stat value={fmtEur(data.best_ca?.value)} name={data.best_ca?.name} label="Meilleur CA cumulé" testid="cw-best-ca" />
          <Stat value={fmtPct(data.best_quality?.value)} name={data.best_quality?.name} label="Meilleure qualité" testid="cw-best-quality" />
          <Stat value={fmtTrend(data.best_trend?.value)} name={data.best_trend?.name} label="Progression N-1" valueClass="text-orange" testid="cw-best-trend" />
        </div>
      )}
      <button
        onClick={load}
        aria-label="Actualiser"
        className="absolute bottom-2 right-2 p-1.5 rounded-full hover:bg-graphite-700 transition-colors z-10"
        title="Actualiser" data-testid="cw-refresh"
      >
        <RefreshCw className={`w-3.5 h-3.5 text-ondark-3 ${loading ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

export default CommercialWeatherWidget;
